import { ReactElement, useCallback, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useLatest } from 'react-use'
import { Vector3 } from 'three'
import { Close } from '@mui/icons-material'
import { IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import usePlanTableMutations from '@editor/hooks/usePlanTableMutations'
import usePlanViewPlanTables from '@editor/pages/PlanView/hooks/usePlanViewPlanTables'
import PlanExportConfigFields from '@editor/pages/StructuralPlanning/components/PlanTableForm/PlanExportConfigFields'
import PlanTableDataEditor from '@editor/pages/StructuralPlanning/components/PlanTableForm/PlanTableDataEditor'
import PlanTablePositionAnchorFields from '@editor/pages/StructuralPlanning/components/PlanTableForm/PlanTablePositionAnchorFields'
import PlanTableTemplateOptionsFields from '@editor/pages/StructuralPlanning/components/PlanTableForm/PlanTableTemplateOptionsFields'
import { Html } from '@react-three/drei'
import { useCanvasEvent, usePlanTableLayoutMetrics } from '@editorHooks'
import { isTemplatePlanTable, type PlanTable } from 'src/domain/planTables/types'

interface Props {
  planTable: PlanTable
  /** World-space anchor (the table's top-right corner) the popup floats next to. */
  anchor: Vector3
  onClose: () => void
}

/**
 * In-scene quick editor for a plan table: name, the table contents (shared
 * `PlanTableDataEditor`, same as the sidebar form) and the plan/storey export
 * config. Mirrors the comment edit popup (a drei `<Html>` panel that closes on a
 * canvas click or Escape). Edits are held locally and persisted once when the
 * popup closes — matching the comment popup and avoiding a backend save per
 * keystroke.
 */
const PlanTableEditPopup = ({ planTable, anchor, onClose }: Props): ReactElement => {
  // The drei <Html> portal renders outside the r3f tree, so the app's MUI theme
  // context doesn't reach it — re-provide it from the theme available here.
  const theme = useTheme()
  const { updatePlanTableFields } = usePlanTableMutations()
  // Resolved out here, in the r3f tree: the <Html> below is its own React root, so hooks reading
  // context (these query the plan settings and the previewed plan's tables) must not run inside it.
  const layoutMetrics = usePlanTableLayoutMetrics()
  const { getTableData } = usePlanViewPlanTables()

  // Pending edits, applied on close. Read via a ref so the close handler (and its
  // canvas/keydown listeners) stays stable while typing.
  const [draft, setDraft] = useState<Partial<PlanTable>>({})
  const draftRef = useLatest(draft)
  const planConfig = draft.planConfig ?? planTable.planConfig
  const table = draft.table ?? planTable.table
  const position = draft.position ?? planTable.position
  const positionAnchor = draft.positionAnchor ?? planTable.positionAnchor
  const templateOptions = draft.templateOptions ?? planTable.templateOptions
  const isTemplateTable = isTemplatePlanTable(planTable)
  // Template content is generated (read-only): show it as the previewed plan draws it.
  const displayedTable = isTemplateTable ? getTableData(planTable) : table

  const handleClose = useCallback(() => {
    const changes = draftRef.current
    if (Object.keys(changes).length > 0) updatePlanTableFields(planTable.id, changes)
    onClose()
  }, [draftRef, planTable.id, updatePlanTableFields, onClose])

  // A left-button pointer-down on the scene canvas persists and closes the popup.
  // Listening on the canvas (not window) means clicks on other UI — including the
  // popup's own inputs — don't close it. Non-left buttons are ignored so a
  // right-button camera drag keeps the popup open.
  const handleCanvasPointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.button !== 0) return
      handleClose()
    },
    [handleClose],
  )
  useCanvasEvent('pointerdown', handleCanvasPointerDown)

  useHotkeys(
    'esc',
    event => {
      event.stopPropagation()
      handleClose()
    },
    { enableOnFormTags: true },
    [handleClose],
  )

  return (
    <Html position={anchor} zIndexRange={[100, 0]} style={{ pointerEvents: 'auto' }}>
      <ThemeProvider theme={theme}>
        <Paper
          elevation={4}
          sx={{ p: 1.5, width: 520, transform: 'translate(16px, 0)' }}
          onPointerDown={event => event.stopPropagation()}
          data-cy="plan-table-edit-popup"
        >
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Tabelle bearbeiten</Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ mr: -0.5, mt: -0.5 }}
                data-cy="plan-table-popup-close"
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>

            <TextField
              label="Name"
              size="small"
              // Uncontrolled: a controlled value would be re-applied by drei's frequent <Html>
              // re-renders, resetting the caret while typing.
              defaultValue={planTable.name}
              onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
              fullWidth
              data-cy="plan-table-popup-name-input"
            />

            <PlanTablePositionAnchorFields
              position={position}
              positionAnchor={positionAnchor}
              table={displayedTable}
              layoutMetrics={layoutMetrics}
              onChange={changes => setDraft(prev => ({ ...prev, ...changes }))}
            />

            <PlanTableDataEditor
              table={displayedTable}
              readOnly={isTemplateTable}
              onTableChange={updater =>
                setDraft(prev => ({ ...prev, table: updater(prev.table ?? planTable.table) }))
              }
            />

            {isTemplateTable && (
              <PlanTableTemplateOptionsFields
                templateKey={planTable.templateKey}
                templateOptions={templateOptions}
                onChange={nextOptions =>
                  setDraft(prev => ({ ...prev, templateOptions: nextOptions }))
                }
              />
            )}

            <PlanExportConfigFields
              planConfig={planConfig}
              onChange={nextConfig => setDraft(prev => ({ ...prev, planConfig: nextConfig }))}
            />
          </Stack>
        </Paper>
      </ThemeProvider>
    </Html>
  )
}

export default PlanTableEditPopup
